import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import campaignService from '../services/campaignService';

/**
 * useCampaignComplianceStatus
 *
 * Subscribes to the campaigns list query and derives compliance_status for
 * a single campaign. Uses useQuery (not getQueryData) so the component
 * re-renders reactively whenever the campaigns cache updates — e.g. right
 * after the advisor sends an invite or a reviewer submits a verdict.
 *
 * Returns:
 *   complianceStatus  — null | 'pending' | 'approved' | 'rejected'
 *   isApproved        — boolean shortcut for export gating
 */
export function useCampaignComplianceStatus(campaignId) {
  const { user } = useAuth();

  const queryKey = user?.id ? ['campaigns', user.id] : ['campaigns', 'anonymous'];

  const { data: campaigns } = useQuery({
    queryKey,
    queryFn: async () => campaignService.getCampaigns(),
    enabled: Boolean(user?.id),
    staleTime: 60 * 1000,
  });

  const campaign = Array.isArray(campaigns)
    ? campaigns.find((c) => (c.id || c._id) === campaignId)
    : null;

  const complianceStatus = campaign?.compliance_status ?? null;

  return {
    complianceStatus,
    isApproved: complianceStatus === 'approved',
  };
}
